import type {
  PageData,
  ImageData,
  MediaItem,
} from '@interfaces';
import { eatAll } from '@putkoff/abstract-utilities';
// Pure, client-safe media builders (no fs) — the single source of truth for
// how a media item becomes schema.org JSON-LD, shared with the content parser.
import { mimeFromExt, youtubeMeta, toVideoObject } from '@/lib/media/schema';

/* -------------------------------------------------- */
/* Helpers */
/* -------------------------------------------------- */

function cleanKeywords(keywords: string | string[] | undefined): string[] {
  if (!keywords) return [];
  const arr = typeof keywords === 'string' ? keywords.split(',') : keywords;
  return arr
    .map(k => eatAll(k, [',', '\n', '\t', '#']))
    .map(k => k.trim())
    .filter(Boolean);
}

/* -------------------------------------------------- */
/* Site identity */
/* -------------------------------------------------- */

const SITE_NAME = 'The Daily Dialectics';
// Site tagline — used in the home <title> and the WebSite JSON-LD description.
// Mirrors the original site's masthead topics (see the 2020 Wayback capture)
// so the brand result in search conveys the site's actual subjects.
const SITE_TAGLINE = 'Sommerfeld-Goubau, Nikola Tesla, MEMs, Cannabinoids, & Science';

// The masthead quick-links (components/Header) — the same five topic anchors
// the original site put under the wordmark on every page, which is what earned
// the sitelinks/nav selection in Google's index. Emitted as
// SiteNavigationElement JSON-LD on every page to reinforce that association.
const SITE_NAV_LINKS: Array<{ name: string; path: string }> = [
  { name: 'Sommerfeld-Goubau', path: '/sommerfeld-goubau/sommerfeld-goubau' },
  { name: 'Nikola Tesla', path: '/tesla/nikola-tesla' },
  { name: 'MEMs', path: '/mems/mems' },
  { name: 'Cannabinoids', path: '/cannabis/medicinal-cannabis' },
  { name: 'Science', path: '/education/educational' },
];
// Real asset: public/imgs/favicon/favicon.webp (1200×627, see its info.json).
// public/imgs is the frozen-media symlink; nginx serves /imgs/ from the same tree.
const SITE_LOGO_PATH = '/imgs/favicon/favicon.webp';
const SITE_LOGO_WIDTH = 1200;
const SITE_LOGO_HEIGHT = 627;

// Trailing brand fragments we never want to double up in <title> templates.
const BRAND_SUFFIX_RX = /\s*[|–—-]\s*(the daily dialectics|tdd)\s*$/i;

/** Strip any trailing "| The Daily Dialectics" / "| TDD" (repeatedly). */
export function stripBrandSuffix(rawTitle: string): string {
  let t = (rawTitle || '').trim();
  while (BRAND_SUFFIX_RX.test(t)) t = t.replace(BRAND_SUFFIX_RX, '').trim();
  return t;
}

/* -------------------------------------------------- */
/* Main Generator */
/* -------------------------------------------------- */

export function generateFullPageMetadata(pageData: PageData, imageData: ImageData) {
  const {
    BASE_URL,
    href,
    title,
    description,
    share_url,
    keywords_str,
    media:rawMedia = [],
    pdfs:rawPdfs = [],
    images:rawImages = {},
    videos:rawVideos = [],
  } = pageData;

  // Canonical must be an absolute URL. A handful of variables.json carry a
  // relative/garbage share_url (e.g. "/imgs/…") — fall back to BASE_URL+href
  // rather than emitting a broken canonical/og:url/mainEntityOfPage.
  const shareUrlAbsolute =
    share_url && /^https?:\/\//i.test(share_url) ? share_url : '';
  const canonicalUrl = (shareUrlAbsolute || `${BASE_URL}${href}`)
    .split('?')[0]
    .split('#')[0];

  // Site-level identity anchors (@id refs shared by every node in the graph).
  const siteBase = (BASE_URL || 'https://thedailydialectics.com').replace(/\/+$/, '');
  const websiteId = `${siteBase}/#website`;
  const orgId = `${siteBase}/#org`;
  const logoUrl = `${siteBase}${SITE_LOGO_PATH}`;
  const isHome = canonicalUrl.replace(/\/+$/, '') === siteBase;

  // <title> template: "<specific title> | The Daily Dialectics" everywhere,
  // homepage gets "The Daily Dialectics — <tagline>". og:/twitter: keep the
  // specific title WITHOUT the brand suffix, and titles that already carry the
  // brand are stripped first so nothing gets double-suffixed.
  const specificTitle = isHome ? SITE_NAME : (stripBrandSuffix(title) || SITE_NAME);
  const pageTitle = isHome
    ? `${SITE_NAME} — ${SITE_TAGLINE}`
    : `${specificTitle} | ${SITE_NAME}`;

  // Real dates only — derived in getStaticProps (utils/page_dates: explicit
  // variables.json field, else content-file mtimes), with the thumbnail's own
  // datePublished as a last fallback. Never new Date(): a modified_time
  // fabricated at build re-dates every article on every deploy.
  const datePublished: string =
    pageData.datePublished || imageData?.schema?.datePublished || '';
  const dateModified: string = pageData.dateModified || datePublished || '';

  const pageKeywords = cleanKeywords(keywords_str);
  const imageKeywords = cleanKeywords(imageData?.keywords_str);
  const combinedKeywords = [...new Set([...pageKeywords, ...imageKeywords, 'The Daily Dialectics', 'the daily dialectics', 'thedailydialectics'])];

  // Resolve the primary image defensively. A thumbnail info.json that is
  // missing `schema` or `dimensions` must never throw here — getStaticProps
  // would catch it and 404 the entire page, silently dropping it from search.
  const primaryDims = imageData?.dimensions ?? ({} as { width?: number; height?: number });
  const primaryImageUrl =
    imageData?.schema?.url ||
    imageData?.social_meta?.['og:image'] ||
    (pageData.thumbnail
      ? `${BASE_URL}/${String(pageData.thumbnail).replace(/^\/+/, '')}`
      : '');

  /* -------------------------------------------------- */
  /* Media Detection */
  /* -------------------------------------------------- */

  const imageObjects: any[] = [];
  const videoObjects: any[] = [];
  const audioObjects: any[] = [];
  const digitalDocuments: any[] = [];
  const safeImages = Array.isArray(rawImages)
    ? rawImages
    : typeof rawImages === 'string'
      ? [rawImages]
      : [];

  const media = Array.isArray(rawMedia) ? rawMedia : [];
  const pdfs = Array.isArray(rawPdfs)
    ? rawPdfs
    : typeof rawPdfs === 'string'
      ? [rawPdfs]
      : [];
  // Primary image first (only if we actually resolved a URL)
  if (primaryImageUrl) {
    imageObjects.push({
      '@type': 'ImageObject',
      url: primaryImageUrl,
      ...(primaryDims.width ? { width: primaryDims.width } : {}),
      ...(primaryDims.height ? { height: primaryDims.height } : {}),
      ...(imageData?.caption ? { caption: imageData.caption } : {}),
      ...(imageData?.license ? { license: imageData.license } : {}),
    });
  }

  // Additional page images — root-relative srcs resolved in getStaticProps
  // (collectPageImages). Absolute URLs pass through; the hero thumbnail is
  // deduped so og:image never lists the same file twice.
  safeImages.forEach(imgPath => {
    const url = /^https?:\/\//i.test(imgPath)
      ? imgPath
      : `${String(BASE_URL || '').replace(/\/+$/, '')}/${String(imgPath).replace(/^\/+/, '')}`;
    if (!imageObjects.some(i => i.url === url)) {
      imageObjects.push({
        '@type': 'ImageObject',
        url,
      });
    }
  });

  // Media items
  media.forEach((item: MediaItem) => {
    const mime = mimeFromExt(item.ext);

    if (item.ext === 'mp4') {
      videoObjects.push({
        '@type': 'VideoObject',
        name: item.title || title,
        description: item.description || description,
        contentUrl: item.url,
        encodingFormat: mime,
      });
    }

    if (item.ext === 'mp3') {
      audioObjects.push({
        '@type': 'AudioObject',
        name: item.title || title,
        contentUrl: item.url,
        encodingFormat: mime,
      });
    }

    if (item.ext === 'pdf') {
      digitalDocuments.push({
        '@type': 'DigitalDocument',
        name: item.title || item.basename,
        url: item.url,
        encodingFormat: 'application/pdf',
      });
    }
  });

  // Explicit pdf array support
  pdfs.forEach(baseId => {
    digitalDocuments.push({
      '@type': 'DigitalDocument',
      name: baseId.replace(/_/g, ' '),
      url: `${BASE_URL}/pdfs/${baseId}/${baseId}.pdf`,
      encodingFormat: 'application/pdf',
    });
  });

  // YouTube embeds — ids/urls extracted from page content at build time
  // (pageData.videos, set in getStaticProps). Each becomes a complete, valid
  // VideoObject (name, description, thumbnailUrl, embedUrl) so the videos a page
  // references are eligible for video rich results instead of being invisible.
  const videos = Array.isArray(rawVideos) ? rawVideos : [];
  videos.forEach((v: any) => {
    const idOrUrl = typeof v === 'string' ? v : (v?.id || v?.url || '');
    const vm = youtubeMeta(idOrUrl, {
      title: (typeof v === 'object' && v?.title) || title,
      description: (typeof v === 'object' && v?.description) || description,
    });
    if (vm) videoObjects.push(toVideoObject(vm, BASE_URL));
  });

  /* -------------------------------------------------- */
  /* Citation Schema */
  /* -------------------------------------------------- */

  const citations =
    (pageData as any).sources?.map((src: any) => ({
      '@type': 'CreativeWork',
      name: src.title,
      url: src.href,
      description: src.description,
    })) || [];

  /* -------------------------------------------------- */
  /* Schema Graph */
  /* -------------------------------------------------- */

  // Organization + WebSite are emitted on EVERY page; Article references them
  // by @id. BreadcrumbList appears when getStaticProps supplied a trail
  // (section/slug pages — same related_index.json data as the visible nav).
  const organizationNode = {
    '@type': 'Organization',
    '@id': orgId,
    name: SITE_NAME,
    alternateName: ['The Daily Dialectics', 'the daily dialectics', 'thedailydialectics', 'TDD'],
    url: `${siteBase}/`,
    logo: {
      '@type': 'ImageObject',
      '@id': `${siteBase}/#logo`,
      url: logoUrl,
      width: SITE_LOGO_WIDTH,
      height: SITE_LOGO_HEIGHT,
    },
    sameAs: ['https://x.com/thedailydialectics'],
  };

  const webSiteNode = {
    '@type': 'WebSite',
    '@id': websiteId,
    url: `${siteBase}/`,
    name: SITE_NAME,
    alternateName: ['The Daily Dialectics', 'the daily dialectics', 'thedailydialectics', 'TDD'],
    description: SITE_TAGLINE,
    publisher: { '@id': orgId },
    inLanguage: 'en-US',
  };

  const siteNavNode = {
    '@type': 'ItemList',
    '@id': `${siteBase}/#sitenav`,
    name: 'Primary topics',
    itemListElement: SITE_NAV_LINKS.map((l, i) => ({
      '@type': 'SiteNavigationElement',
      position: i + 1,
      name: l.name,
      url: `${siteBase}${l.path}`,
    })),
  };

  const breadcrumbs = Array.isArray(pageData.breadcrumbs) ? pageData.breadcrumbs : [];
  const breadcrumbNode = breadcrumbs.length
    ? {
        '@type': 'BreadcrumbList',
        '@id': `${canonicalUrl}#breadcrumb`,
        itemListElement: breadcrumbs.map((crumb, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: crumb.name,
          item: crumb.item,
        })),
      }
    : null;

  // The homepage is the site's front door, not an article — it gets a WebPage
  // node (and og:type "website") so the brand query's canonical result is
  // unambiguous. Every other page is an Article as before.
  const articleNode = {
    '@type': isHome ? 'WebPage' : 'Article',
    '@id': `${canonicalUrl}#${isHome ? 'webpage' : 'article'}`,
    headline: specificTitle,
    description,
    url: canonicalUrl,
    ...(isHome ? {} : { mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl } }),
    ...(datePublished ? { datePublished } : {}),
    ...(dateModified ? { dateModified } : {}),
    keywords: combinedKeywords,
    image: imageObjects,
    video: videoObjects.length ? videoObjects : undefined,
    audio: audioObjects.length ? audioObjects : undefined,
    hasPart: digitalDocuments.length ? digitalDocuments : undefined,
    citation: citations.length ? citations : undefined,
    isPartOf: { '@id': websiteId },
    author: { '@id': orgId },
    publisher: { '@id': orgId },
  };

  const schemaGraph = {
    '@context': 'https://schema.org',
    '@graph': [
      organizationNode,
      webSiteNode,
      siteNavNode,
      ...(breadcrumbNode ? [breadcrumbNode] : []),
      articleNode,
    ],
  };

  /* -------------------------------------------------- */
  /* OpenGraph Multi Image */
  /* -------------------------------------------------- */

  // Shared values that drive rich previews on every platform that reads
  // Open Graph / Twitter cards (Facebook, LinkedIn, Slack, Discord, WhatsApp,
  // Telegram, Pinterest, iMessage, …).
  const primaryImage = imageObjects[0]?.url || '';
  const imageAlt = imageData?.alt || pageData.thumbnail_alt || title;
  const imageType = imageData?.ext ? mimeFromExt(imageData.ext) : '';
  const section = ((href || '').replace(/^\/+/, '').split('/')[0] || 'General')
    .split(/[-_]/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  const TWITTER_HANDLE = '@thedailydialectics';
  const FB_APP_ID = '427305388009806';

  const openGraph: Record<string, string | string[]> = {
    'og:type': isHome ? 'website' : 'article',
    'og:title': specificTitle,
    'og:description': description,
    'og:url': canonicalUrl,
    'og:site_name': SITE_NAME,
    'og:locale': 'en_US',
    ...(dateModified ? { 'og:updated_time': dateModified } : {}),
    'fb:app_id': FB_APP_ID,
    'og:image': imageObjects.map(i => i.url),
    ...(imageAlt ? { 'og:image:alt': imageAlt } : {}),
    ...(primaryImage.startsWith('https') ? { 'og:image:secure_url': primaryImage } : {}),
    ...(imageType ? { 'og:image:type': imageType } : {}),
    ...(primaryDims.width  ? { 'og:image:width':  String(primaryDims.width)  } : {}),
    ...(primaryDims.height ? { 'og:image:height': String(primaryDims.height) } : {}),
    ...(datePublished ? { 'article:published_time': datePublished } : {}),
    ...(dateModified ? { 'article:modified_time': dateModified } : {}),
    'article:author': 'The Daily Dialectics',
    'article:section': section,
    ...(combinedKeywords.length ? { 'article:tag': combinedKeywords } : {}),
  };

  /* -------------------------------------------------- */
  /* Twitter */
  /* -------------------------------------------------- */

  const twitter: Record<string, string> = {
    'twitter:card': 'summary_large_image',
    'twitter:title': specificTitle,
    'twitter:description': description,
    'twitter:site': TWITTER_HANDLE,
    'twitter:creator': TWITTER_HANDLE,
    ...(primaryImage ? { 'twitter:image': primaryImage } : {}),
    ...(imageAlt ? { 'twitter:image:alt': imageAlt } : {}),
  };

  /* -------------------------------------------------- */
  /* General Meta */
  /* -------------------------------------------------- */

  const meta = {
    description,
    keywords: combinedKeywords.join(', '),
    author: 'The Daily Dialectics',
    robots: 'index, follow',
    'theme-color': '#ffffff',
  };

  return {
    canonicalUrl,
    /** Full <title> value — brand-templated, never double-suffixed. */
    pageTitle,
    /** The specific (brandless) title used for og:/twitter: cards. */
    specificTitle,
    primaryImage,
    openGraph,
    twitter,
    meta,
    schemaGraph,
  };
}